import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import SortableCreator from './SortableCreator';
import { searchCircleCreators } from '../../api/circles';
import CreatorInfo from './CreatorInfo';
import { message, moveMember } from './circle_helpers';

function CircleCreators({ members, onChange }) {
  const [memberQuery, setMemberQuery] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [undo, setUndo] = useState(null);
  const [dragging, setDragging] = useState(false);
  const filtered = !!memberQuery.trim();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const creatorName = id => members.find(member => member.User_id === id).user.name;
  const position = id => `position ${members.findIndex(member => member.User_id === id) + 1} of ${members.length}`;
  const announcements = {
    onDragStart: ({ active }) => `Picked up ${creatorName(active.id)} from ${position(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over ? `${creatorName(active.id)} is over ${position(over.id)}.` : `${creatorName(active.id)} is no longer over the lineup.`,
    onDragEnd: ({ active, over }) =>
      over && over.id !== active.id
        ? `${creatorName(active.id)} moved to ${position(over.id)}.`
        : `${creatorName(active.id)} returned to ${position(active.id)}.`,
    onDragCancel: ({ active }) => `Reordering cancelled. ${creatorName(active.id)} returned to ${position(active.id)}.`
  };
  const visibleMembers = members.filter(member =>
    [member.user.name, member.user.username, member.user.social_links, member.User_id]
      .join(' ')
      .toLowerCase()
      .includes(memberQuery.trim().toLowerCase())
  );
  const unavailable = members.filter(member => !member.user.username).length;
  useEffect(() => {
    let current = true;
    setResults([]);
    setError('');
    setLoading(!!query.trim());
    const timer = setTimeout(() => {
      if (!query.trim()) return;
      searchCircleCreators(query)
        .then(data => current && setResults(data.creators))
        .catch(error => current && setError(message(error)))
        .finally(() => current && setLoading(false));
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [query]);
  const remove = ids => {
    setUndo(members);
    onChange(members.filter(member => !ids.includes(member.User_id)));
    setSelected([]);
    setSelecting(false);
  };
  return (
    <div
      className='circle-curation'
      onKeyDown={event => {
        if (dragging && event.key === 'Escape') event.preventDefault();
      }}
    >
      <div className='circle-curation-heading'>
        <div>
          <h3>Your creator lineup</h3>
          <p>Review the mix, explore their shops, and choose who belongs.</p>
        </div>
        <span>{members.length} creators</span>
      </div>
      <div className='circle-curation-controls'>
        <label>
          Find in this Circle
          <input value={memberQuery} placeholder='Name, handle, or creator ID' onChange={event => setMemberQuery(event.target.value)} />
        </label>
        <button
          onClick={() => {
            setSelecting(!selecting);
            setSelected([]);
          }}
        >
          {selecting ? 'Cancel selection' : 'Select creators'}
        </button>
        {selecting && (
          <button disabled={!selected.length} onClick={() => remove(selected)}>
            Remove selected ({selected.length})
          </button>
        )}
      </div>
      {!!unavailable && (
        <p className='circle-curation-notice'>
          {unavailable} {unavailable === 1 ? 'member is' : 'members are'} unavailable in the public creator list. Their membership is preserved until
          you remove them.
        </p>
      )}
      {selecting && !!visibleMembers.length && (
        <label className='circle-check'>
          <input
            type='checkbox'
            checked={visibleMembers.every(member => selected.includes(member.User_id))}
            onChange={event => {
              const ids = visibleMembers.map(member => member.User_id);
              setSelected(event.target.checked ? [...new Set([...selected, ...ids])] : selected.filter(id => !ids.includes(id)));
            }}
          />
          Select all visible creators ({visibleMembers.length})
        </label>
      )}
      {filtered && <p className='circle-reorder-hint'>Clear your search to reorder the full lineup.</p>}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        accessibility={{ announcements }}
        onDragStart={() => setDragging(true)}
        onDragCancel={() => setDragging(false)}
        onDragEnd={({ active, over }) => {
          setDragging(false);
          if (!over || filtered) return;
          const next = moveMember(members, active.id, over.id);
          if (next === members) return;
          onChange(next);
          setUndo(null);
        }}
      >
        <SortableContext items={visibleMembers.map(member => member.User_id)} strategy={rectSortingStrategy}>
          <div className='circle-creator-grid'>
            {visibleMembers.map(member => (
              <SortableCreator
                key={member.User_id}
                member={member}
                selecting={selecting}
                selected={selected.includes(member.User_id)}
                onSelect={event => setSelected(event.target.checked ? [...selected, member.User_id] : selected.filter(id => id !== member.User_id))}
                onRemove={() => remove([member.User_id])}
                disabled={filtered}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {!members.length && <p className='circle-curation-empty'>No creators added yet. Search below to start your lineup.</p>}
      {!!members.length && !visibleMembers.length && (
        <p className='circle-curation-empty'>No members match this search. Try another name, handle, or ID.</p>
      )}
      {undo && (
        <div className='circle-curation-undo' role='status'>
          Creators removed from your draft.{' '}
          <button
            onClick={() => {
              onChange(undo);
              setUndo(null);
            }}
          >
            Undo removal
          </button>
        </div>
      )}
      <div className='circle-curation-add'>
        <h3>Expand your Circle</h3>
        <label>
          Add Creators
          <input placeholder='Name, ShopMy username, or social handle' value={query} onChange={event => setQuery(event.target.value)} />
        </label>
        {members.length >= 100 && <p>This Circle has reached the 100-creator limit for adding members.</p>}
        {loading && <p role='status'>Searching creators…</p>}
        {!loading && query.trim() && !results.length && !error && <p>No creators found.</p>}
        {error && <p role='alert'>{error}</p>}
        <div className='circle-creator-grid'>
          {results.map(user => {
            const added = members.some(member => member.User_id === user.id);
            return (
              <article className='circle-creator-card' key={user.id}>
                <CreatorInfo user={user} />
                <button
                  disabled={added || members.length >= 100}
                  onClick={() => {
                    onChange([...members, { User_id: user.id, user, description: '' }]);
                    setUndo(null);
                  }}
                >
                  {added ? 'Added' : 'Add creator'}
                </button>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

CircleCreators.propTypes = { members: PropTypes.array.isRequired, onChange: PropTypes.func.isRequired };

export default CircleCreators;
